import React from 'react';
import styled from 'styled-components';

const HeroSection = styled.section`
  background: linear-gradient(135deg, ${props => props.theme.colors.primary} 0%, ${props => props.theme.colors.primaryDark} 100%);
  padding: 6rem 0 4rem;
  position: relative;
  overflow: hidden;
  
  /* Background image with overlay */
  background-image: 
    linear-gradient(
      135deg, 
      rgba(1, 103, 95, 0.9), 
      rgba(0, 82, 76, 0.85)
    ),
    url('/Course 3 Background 1.webp');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  
  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    padding: 5rem 0 3rem;
    background-position: center 30%;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    padding: 4.5rem 0 2.5rem;
    background-position: center 25%;
  }

  @media (max-width: 768px) {
    padding: 4rem 0 2rem;
    background-position: center 20%;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="25" cy="25" r="1.5" fill="white" opacity="0.15"/><circle cx="75" cy="75" r="1.5" fill="white" opacity="0.15"/><circle cx="50" cy="10" r="1" fill="white" opacity="0.1"/><circle cx="10" cy="60" r="1" fill="white" opacity="0.1"/><circle cx="90" cy="40" r="1" fill="white" opacity="0.1"/><circle cx="35" cy="85" r="0.8" fill="white" opacity="0.08"/><circle cx="65" cy="15" r="0.8" fill="white" opacity="0.08"/><circle cx="15" cy="35" r="0.6" fill="white" opacity="0.06"/><circle cx="85" cy="65" r="0.6" fill="white" opacity="0.06"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
    opacity: 0.4;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
`;

const HeroContent = styled.div`
  text-align: center;
  color: white;
`;

const CourseCode = styled.div`
  background: ${props => props.theme.colors.secondary};
  color: ${props => props.theme.colors.primary};
  padding: 0.5rem 1.5rem;
  border-radius: ${props => props.theme.borderRadius.full};
  font-weight: ${props => props.theme.fontWeights.semibold};
  font-size: ${props => props.theme.fontSizes.sm};
  display: inline-block;
  margin-bottom: 1.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CourseTitle = styled.h1`
  font-size: ${props => props.theme.fontSizes['5xl']};
  font-weight: ${props => props.theme.fontWeights.bold};
  margin-bottom: 2rem;
  line-height: 1.2;
  color: white;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.fontSizes['4xl']};
  }
`;

const CourseOverview = styled.p`
  font-size: ${props => props.theme.fontSizes.xl};
  line-height: 1.6;
  max-width: 800px;
  margin: 0 auto 3rem;
  opacity: 0.95;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.fontSizes.lg};
  }
`;

const HeroStats = styled.div`
  display: flex;
  justify-content: center;
  gap: 3rem;
  flex-wrap: wrap;
  margin-top: 3rem;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    gap: 2rem;
  }
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: ${props => props.theme.fontSizes['3xl']};
  font-weight: ${props => props.theme.fontWeights.bold};
  color: ${props => props.theme.colors.secondary};
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: ${props => props.theme.fontSizes.sm};
  opacity: 0.8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ScrollIndicator = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  animation: bounce 2s infinite;
  
  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% {
      transform: translateX(-50%) translateY(0);
    }
    40% {
      transform: translateX(-50%) translateY(-10px);
    }
    60% {
      transform: translateX(-50%) translateY(-5px);
    }
  }
`;

const ScrollIcon = styled.div`
  width: 30px;
  height: 30px;
  border: 2px solid white;
  border-top: none;
  border-left: none;
  transform: rotate(45deg);
  opacity: 0.7;
`;

const CourseHero = ({ courseDetail }) => {
  if (!courseDetail) return null;

  return (
    <HeroSection>
      <Container>
        <HeroContent>
          <CourseCode>{courseDetail.courseCode}</CourseCode>
          <CourseTitle>{courseDetail.courseTitle}</CourseTitle>
          <CourseOverview>{courseDetail.courseOverview}</CourseOverview>
          
          <HeroStats>
            <StatItem>
              <StatNumber>RPL</StatNumber>
              <StatLabel>Pathway</StatLabel>
            </StatItem>
            <StatItem>
              <StatNumber>100%</StatNumber>
              <StatLabel>Online</StatLabel>
            </StatItem>
            <StatItem>
              <StatNumber>Fast</StatNumber>
              <StatLabel>Processing</StatLabel>
            </StatItem>
          </HeroStats>
        </HeroContent>
      </Container>
      
      <ScrollIndicator>
        <ScrollIcon />
      </ScrollIndicator>
    </HeroSection>
  );
};

export default CourseHero; 